import clsx from "clsx";
import { useState } from "react";
import {
    CustomTable,
    TableTypes,
} from "@/components/Home/TradingChartInfoSection/analytics/shared/ui/table";
import PaginationButton from "./expand-button/PaginationButton";
import { AnalystInfo } from "@/components/Home/TradingChartInfoSection/analytics/shared/ui/analyst-card";
import { SuccessRateInfo } from "../../../shared/ui/analyst-card/success-rate/SuccessRateInfo";
import styles from "./AnalystForecasts.module.scss";
import { useTranslation } from "next-i18next";

export const AnalystForecasts = ({
    data,
    companyName,
}: {
    data: {
        analystInfo: {
            name: string;
            rating: number;
            company: string;
            avatar: string | null;
            stockAvgReturn: number;
        };
        position: string;
        priceTarget: number;
        action: string;
        date: string;
        stockTotalRecommendations: number;
        stockGoodRecommendations: number;
    }[];
    companyName: string;
}) => {
    const [isShowAll, setShowAll] = useState(false);
    const { t } = useTranslation("common");

    const displayAverageReturn = (avgReturn: number | null) => {
        if (avgReturn === null) return t("analytics.common.notAvailable");
        if (avgReturn === 0) return t("analytics.common.zeroPercent");
        return `${avgReturn > 0 ? "+" : ""}${avgReturn}%`;
    };

    return (
        <div className={styles.analystForecastsContainer}>
            <CustomTable
                type={TableTypes.analystForecasts}
                data={data}
                renderRowHoverComponent={rowData => {
                    return (
                        <div className={styles.tooltip}>
                            <AnalystInfo
                                company={rowData.analystInfo.company}
                                name={rowData.analystInfo.name}
                                rating={rowData.analystInfo.rating}
                                avatar={rowData.analystInfo.avatar}
                                theme="light"
                            />
                            <SuccessRateInfo
                                total={rowData.stockTotalRecommendations}
                                success={rowData.stockGoodRecommendations}
                                themeColor="light"
                            />
                            <div className={styles.averageReturn}>
                                <div className={styles.titles}>
                                    <div className={styles.title}>
                                        {t("analytics.analystForecasts.averageReturn.title")}
                                    </div>
                                    <div className={styles.subtitle}>
                                        {t("analytics.analystForecasts.averageReturn.subtitle")}
                                    </div>
                                </div>
                                <div
                                    className={clsx(styles.averageReturnValue, {
                                        [styles.negative]: rowData.analystInfo.stockAvgReturn < 0,
                                    })}>
                                    {displayAverageReturn(rowData.analystInfo.stockAvgReturn)}
                                </div>
                            </div>
                            <div className={styles.rating}>
                                <div className={styles.title}>
                                    {t("analytics.analystForecasts.rating.title")}
                                </div>
                                <div>
                                    {t("analytics.analystForecasts.rating.description", {
                                        position: rowData.position,
                                        companyName,
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                }}
            />
            {data?.length > 3 && (
                <PaginationButton
                    title={
                        isShowAll
                            ? t("analytics.analystForecasts.pagination.hideAll")
                            : t("analytics.analystForecasts.pagination.viewAll")
                    }
                    onClick={() => {
                        setShowAll(prev => !prev);
                    }}
                />
            )}
        </div>
    );
};
